import { useMemo } from 'react';

import type { BaseRowData, LegacyDataProvider, LegacyDataProviderRow, LegacyDataProviderRowData } from '../../types';

const DEFAULT_ROW_COUNT = 100;

/**
 * Хук, возвращающий loading-датапровайдер.
 * Loading-датапровайдер – это датапровайдер, данные которого всегда находятся в состоянии загрузки.
 * Такой датапровайдер удобно использовать для создания стаба всего грида, в то время как данные из
 * endpoint-датапровайдера еще загружаются.
 *
 * @typeParam RowData Данные строки.
 * @param count Количество элементов для отображения при загрузке.
 * @returns Провайдер данных грида.
 * @deprecated Используйте flow-based {@link createLoadingDataProvider}.
 */
export function useLegacyLoadingDataProvider<RowData extends BaseRowData>(
    count: number = DEFAULT_ROW_COUNT
): LegacyDataProvider<RowData> {
    return useMemo(() => {
        const loadedRows: RowData[] = [];
        const rows: LegacyDataProviderRowData[] = Array.from({ length: count }, (_, index) => ({
            id: `not_loaded_${index}`,
            status: 'not-loaded',
        }));

        const dataProvider: LegacyDataProvider<RowData> = {
            totalCount: count,
            useRowData,
            getRows: () => rows,
            getLoadedRows: () => loadedRows,
        };

        return dataProvider;
    }, [count]);
}

function useRowData<T>() {
    const row: LegacyDataProviderRow<T> = {
        data: undefined,
        isLoading: true,
        isError: false,
        isFetching: false,
    };
    return row;
}
